import React, { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import api from '@/lib/api';
import { useT } from '@/lib/i18n';
import { toast } from 'sonner';
import { ArrowLeft } from 'lucide-react';

import { VideoPreview, EditorPanels, ScorePanel } from './editor/EditorParts';
import { useClipExport } from './editor/useClipExport';
import AIInsightCard from './editor/AIInsightCard';
import AIAssistantPanel from './clip-ai/AIAssistantPanel';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const Editor = () => {
  const { id } = useParams();
  const { t } = useT();
  const videoRef = useRef(null);
  const [clip, setClip] = useState(null);
  const [caption, setCaption] = useState('');
  const [color, setColor] = useState('#D4FF00');
  const [zoom, setZoom] = useState([100]);
  const [music, setMusic] = useState('viral_beat_01');
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [useSyncedSubs, setUseSyncedSubs] = useState(true);
  const [captionsEnabled, setCaptionsEnabled] = useState(true);
  // Subtitle Studio V2 state — bypasses legacy caption path when preset_id !== 'legacy'.
  const [presetId, setPresetId] = useState('hormozi');
  const [platform, setPlatform] = useState('tiktok');
  const [verticalOffset, setVerticalOffset] = useState(550);
  // Shared studio preview state — lifted here so BOTH the studio panel AND
  // the main VideoPreview can render the same segmented lines with the same
  // active preset. Every preset switch mutates these two → the on-screen
  // caption overlay updates within the debounced preview window (~250 ms).
  const [studioPreview, setStudioPreview] = useState(null);
  const [studioActivePreset, setStudioActivePreset] = useState(null);
  const handleStudioPreview = React.useCallback((prev, preset) => {
    setStudioPreview(prev);
    setStudioActivePreset(preset);
  }, []);

  const { exporting, exportClip } = useClipExport(t);

  useEffect(() => {
    api.get(`/clips/${id}`).then((r) => {
      setClip(r.data);
      setCaption(r.data.caption_preview || r.data.title || '');
    }).catch(() => toast.error(t('ed.notfound')));
  }, [id, t]);

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) { v.play(); setPlaying(true); } else { v.pause(); setPlaying(false); }
  };

  const toggleMute = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
  };

  if (!clip) return <div className="p-10 text-zinc-500">{t('vc.loading')}</div>;

  const videoUrl = clip.has_file ? `${API}/clips/${clip.id}/stream` : null;

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10" data-testid="editor-page">
      <Link to={`/videos/${clip.video_id}`} className="inline-flex items-center gap-2 text-sm text-zinc-500 hover:text-white mb-6">
        <ArrowLeft className="w-4 h-4" /> {t('ed.back')}
      </Link>

      <div className="grid lg:grid-cols-12 gap-6">
        <div className="lg:col-span-7">
          <VideoPreview
            clip={clip}
            videoRef={videoRef}
            videoUrl={videoUrl}
            zoom={zoom}
            useSyncedSubs={useSyncedSubs}
            captionsEnabled={captionsEnabled}
            caption={caption}
            color={color}
            currentTime={currentTime}
            duration={duration}
            playing={playing}
            muted={muted}
            exporting={exporting}
            studioPreview={studioPreview}
            activePreset={studioActivePreset}
            verticalOffsetPx={verticalOffset}
            onPlay={() => setPlaying(true)}
            onPause={() => setPlaying(false)}
            onTimeUpdate={(e) => setCurrentTime(e.currentTarget.currentTime)}
            onLoadedMetadata={(e) => setDuration(e.currentTarget.duration)}
            onError={() => toast.error('Erreur de chargement vidéo')}
            togglePlay={togglePlay}
            toggleMute={toggleMute}
            exportClip={() => exportClip(clip, color, useSyncedSubs, captionsEnabled, { presetId, platform, verticalOffset })}
            exportLabel={t('ed.export')}
          />
        </div>

        <div className="lg:col-span-5">
          <EditorPanels
            t={t}
            clip={clip}
            caption={caption}
            setCaption={setCaption}
            color={color}
            setColor={setColor}
            zoom={zoom}
            setZoom={setZoom}
            music={music}
            setMusic={setMusic}
            useSyncedSubs={useSyncedSubs}
            setUseSyncedSubs={setUseSyncedSubs}
            captionsEnabled={captionsEnabled}
            setCaptionsEnabled={setCaptionsEnabled}
            presetId={presetId} setPresetId={setPresetId}
            platform={platform} setPlatform={setPlatform}
            verticalOffset={verticalOffset} setVerticalOffset={setVerticalOffset}
            onPreview={handleStudioPreview}
          />
          <ScorePanel t={t} clip={clip} />
          <AIInsightCard clip={clip} />
        </div>
      </div>

      <AIAssistantPanel clip={clip} />
    </div>
  );
};

export default Editor;
